/**
 * Apply the rules of the English language to pluralise a string based on a
 * specified count. Add rules for abnormal pluralisation as needed.
 */
export function pluralise(nameOfEntity: string, numberOfEntities: number): string {
  if (numberOfEntities === 1) return nameOfEntity;

  if (nameOfEntity.endsWith('ss')) {
    return `${nameOfEntity}es`; // ends with ss, e.g. class becomes classes
  }

  const vowels = ['a', 'e', 'i', 'o', 'u'];
  if (nameOfEntity.endsWith('y') && !vowels.includes(nameOfEntity[nameOfEntity.length - 2])) {
    // ends with a consonant and then y, e.g. category becomes categories
    return `${nameOfEntity.slice(0, nameOfEntity.length - 1)}ies`;
  }

  switch (nameOfEntity) {
    case 'has':
      return 'have';
    case 'this':
      return 'these';
    case 'is':
      return 'are';
    default:
      return `${nameOfEntity}s`;
  }
}

/**
 * Converts a camelcase string to a space seperated string where each word is
 * capitialised.
 */
export function camelcaseToTitlecase(camelCaseString: string): string {
  function isCharacterUpperCase(character: string): boolean {
    return character.toUpperCase() === character && character.toLowerCase() !== character;
  }

  return camelCaseString
    .split('')
    .map((character, index) => {
      if (index === 0) {
        return character.toUpperCase();
      }
      if (index !== 0 && isCharacterUpperCase(character)) {
        return ` ${character.toUpperCase()}`;
      }
      return character.toLowerCase();
    })
    .join('');
}

/**
 * Converts a string to a lowercase, hyphen seperated string, where each space
 * is replaced by a hyphen.
 */
export function slugify(spaceSeparatedString: string): string {
  return spaceSeparatedString.toLowerCase().split(' ').join('-');
}

/**
 * Converts a list of strings into a comma seperated string, with a conjunction
 * inserted before the last string in the list.
 */
export function joinWithConjunction(list: string[], conjunction: 'and' | 'or'): string {
  if (list.length < 2) {
    return list.toString();
  }
  if (list.length === 2) {
    return list.join(` ${conjunction} `);
  }
  return list.reduce(
    (accumulator, currentValue, index) =>
      `${accumulator}${currentValue}` +
      `${index !== list.length - 1 ? ', ' : ''}` +
      `${index === list.length - 2 ? `${conjunction} ` : ''}`,
    ''
  );
}
